// Shared pieces of the broadcast-style side views (TennisStadium,
// BasketArena): perspective projection of a court, generated crowd texture,
// scrolling ad-boards geometry and the fixed-stage scaling.

// A court seen from the side: its far sideline is a line at farY spanning
// farW px, the near one at nearY spanning nearW px; depth in between is
// true perspective (1/width is linear in depth).
// u: 0 left baseline -> 1 right baseline; v: 0 far sideline -> 1 near one.
export function makeProjection({ farY, nearY, farW, nearW, farCx, nearCx }) {
  const depthToScreen = v => {
    const zF = 1 / farW;
    const zN = 1 / nearW;
    const w = 1 / (zF + v * (zN - zF));
    return (w - farW) / (nearW - farW);
  };
  const projS = (u, s) => {
    const w = farW + s * (nearW - farW);
    return {
      x: farCx + s * (nearCx - farCx) + (u - 0.5) * w,
      y: farY + s * (nearY - farY),
      w
    };
  };
  const proj = (u, v) => projS(u, depthToScreen(v));
  return { proj, projS, depthToScreen };
}

export const pt = p => p.x.toFixed(1) + ',' + p.y.toFixed(1);

// Sirplay logo, horizontal variant (small lion, big wordmark): painted on
// every floor, and the ad-board logo when the skin has none. The dark
// version is for the ice, where the white wordmark would vanish.
export { default as PITCH_LOGO } from '@/assets/images/logo-sirplay-pitch.svg';
export { default as PITCH_LOGO_DARK } from '@/assets/images/logo-sirplay-pitch-dark.svg';
export { default as BOARD_LOGO } from '@/assets/images/logo-sirplay-pitch.svg';
// Width / height of the pitch logo (its viewBox).
export const PITCH_LOGO_RATIO = 492 / 136;

// The Sirplay logo on the field is shown only when the skin enables the
// Sirplay licence (settings/getShowSirplayLicence).
export const sirplayLicenceMixin = {
  computed: {
    showSirplayLogo() {
      return this.$store.getters['settings/getShowSirplayLicence'] === true;
    }
  }
};

// Transform that lays an image drawn at x/y -180..180 / -50..50 flat on the
// ground (the image has to be drawn that big: Chrome rasterizes an SVG
// <image> at its own size before the transform, a 1x1 one comes out blank),
// centred on (a, b) and 2*da x 2*db big, where project(a, b) -> {x, y} is
// the scene's ground projection. It is the local affine approximation of
// the perspective: exact enough for a decal this small.
export function floorDecal(project, a, b, da, db) {
  const c = project(a, b);
  const r = project(a + da, b);
  const l = project(a - da, b);
  const n = project(a, b + db);
  const f = project(a, b - db);
  return `matrix(${((r.x - l.x) / 360).toFixed(4)},${((r.y - l.y) / 360).toFixed(4)},${((n.x - f.x) / 100).toFixed(4)},${((n.y - f.y) / 100).toFixed(4)},${c.x.toFixed(1)},${c.y.toFixed(1)})`;
}

// floorDecal for the pitch logo: centred on (a, b), 2*db deep, and as wide
// as it takes to keep the logo's proportions on screen.
export function floorLogo(project, a, b, db) {
  const n = project(a, b + db);
  const f = project(a, b - db);
  const h = Math.hypot(n.x - f.x, n.y - f.y);
  const e = 1e-3;
  const r = project(a + e, b);
  const l = project(a - e, b);
  const pxPerA = Math.hypot(r.x - l.x, r.y - l.y) / (2 * e);
  return floorDecal(project, a, b, h * PITCH_LOGO_RATIO / 2 / pxPerA, db);
}
export const seg = (a, b) => 'M' + pt(a) + ' L' + pt(b);

// Crowd texture: generated once per palette, shared by every instance.
const crowdCache = {};
const CROWD_PALETTES = {
  cool: {
    bg: '#1a242e',
    shirts: ['#5d6f80', '#8795a3', '#3c4a57', '#c9d2da', '#2a3540', '#6f7f8e', '#a7b3be', '#48586a']
  },
  arena: {
    bg: '#15151c',
    shirts: ['#6b5f7a', '#9a8ea8', '#3f3a4c', '#d6d0de', '#2a2733', '#8a4a4a', '#c0a27a', '#4d5a70']
  }
};
export function crowdTexture(kind = 'cool') {
  if (crowdCache[kind] !== undefined) return crowdCache[kind];
  const pal = CROWD_PALETTES[kind] || CROWD_PALETTES.cool;
  let url = '';
  try {
    const w = 180, h = 90;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.fillStyle = pal.bg;
    ctx.fillRect(0, 0, w, h);
    const skins = ['#c9a88c', '#a88468', '#7a5a44', '#d9bda5'];
    for (let y = 0; y < h; y += 4) {
      const off = (y / 4) % 2 ? 1.5 : 0;
      for (let x = 0; x < w; x += 3) {
        if (Math.random() < 0.15) continue;
        ctx.fillStyle = pal.shirts[Math.floor(Math.random() * pal.shirts.length)];
        ctx.fillRect(x + off, y + 1.5, 2, 2.5);
        ctx.fillStyle = skins[Math.floor(Math.random() * skins.length)];
        ctx.fillRect(x + off + 0.5, y, 1, 1.5);
      }
    }
    url = c.toDataURL();
  } catch (e) {
    url = '';
  }
  crowdCache[kind] = url;
  return url;
}

// One ad-board standing on the segment a -> b (screen px), hA / hB px tall
// at each end, carrying `count` logos per board length. Logos are laid out
// twice so the scroll loop (one board length) has no seam.
export function makeBoard(key, a, b, hA, hB, count, item) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  const slots = [];
  for (let i = 0; i < count * 2; i++) slots.push(len * (i + 0.5) / count);
  return {
    key,
    points: [a, b, { x: b.x, y: b.y - hB }, { x: a.x, y: a.y - hA }].map(pt).join(' '),
    top: [{ x: a.x, y: a.y - hA }, { x: b.x, y: b.y - hB }, { x: b.x, y: b.y - hB + 1.5 }, { x: a.x, y: a.y - hA + 1.5 }].map(pt).join(' '),
    matrix: `matrix(${(dx / len).toFixed(4)},${(dy / len).toFixed(4)},0,1,${a.x.toFixed(1)},${a.y.toFixed(1)})`,
    clip: [[0, 0], [len, 0], [len, -hB], [0, -hA]].map(p => p.join(',')).join(' '),
    slots,
    // Room for one logo along the board.
    slot: len / count,
    len,
    // Same scrolling speed on every board (px per second).
    dur: (len / 22).toFixed(1),
    h: (hA + hB) / 2,
    item
  };
}

// Fixed-size stage (this.STAGE_W x this.STAGE_H) scaled to the container
// width and zoomed in by this.ZOOM, the extra width cropped off both sides.
// Needs a `root` ref on the container.
export const stageScaleMixin = {
  data() {
    return { scale: 1, offsetX: 0, resizeObserver: null };
  },
  mounted() {
    this.updateScale();
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.updateScale());
      this.resizeObserver.observe(this.$refs.root);
    } else {
      window.addEventListener('resize', this.updateScale);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener('resize', this.updateScale);
  },
  methods: {
    updateScale() {
      const el = this.$refs.root;
      if (!el) return;
      const w = el.clientWidth || this.STAGE_W;
      this.scale = w / this.STAGE_W * (this.ZOOM || 1);
      this.offsetX = (w - this.STAGE_W * this.scale) / 2;
    }
  }
};
